import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SearchInput } from "@altek/ui/components/data/SearchInput";
import { Banner } from "@altek/ui/components/feedback/Banner";
import { EmptyState } from "@altek/ui/components/feedback/EmptyState";
import { SkCircle, SkLine } from "@altek/ui/components/feedback/Skeleton";
import { SkeletonFrame } from "@altek/ui/components/feedback/SkeletonFrame";
import { TextField } from "@altek/ui/components/form/TextField";
import { Avatar } from "@altek/ui/components/ui/Avatar";
import { Button } from "@altek/ui/components/ui/Button";
import { SegmentedControl } from "@altek/ui/components/form/ChoiceControls";
import { toast } from "@altek/ui/lib/utils/toast";
import { useAuth } from "@/app/providers/AuthProvider";
import { chatApi } from "../api/chat.api";
import { chatPeopleQuery } from "../api/chat.queries";
import { CHAT_PERMISSIONS } from "../api/chat.types";
import type { DirectoryPersonRow } from "../api/chat.types";
import { invalidateChat } from "../lib/chat-invalidate";
import { chatErrorMessage } from "../lib/chat-errors";
import { personPhotoUrl } from "../lib/chat-map";
import { personSubtitle } from "../lib/chat-people";

/** Kişi aramasının eşleştirdiği alanlar — rehberin arama kutusuyla aynı söz. */
const PEOPLE_SEARCH_PLACEHOLDER = "Ad, kullanıcı adı veya e-posta";

/**
 * YENİ SOHBET — TEK BİLEŞEN, İKİ YÜZEY.
 *
 * Sohbet sayfası ile köşedeki yuva aynı şeyi soruyor: kiminle konuşacaksın. Eskiden iki ayrı
 * cevabı vardı — sayfada bir KİP (modal, açılır kutulu seçici), yuvada bir SÜTUN (satır listesi)
 * — ve ikisi ayrı dosyalardı. Bir düzeltme birine yapılıp ötekine yapılmadığında fark, ancak
 * kullanıcı iki yüzeyi yan yana görünce ortaya çıkıyordu. Artık ikisi de bu bileşeni çiziyor;
 * aradaki tek fark KAPSAYICININ genişliği, yani bileşenin hiç bilmediği bir şey.
 *
 * KİP DEĞİL SÜTUN, çünkü yuvada modal açmak zaten örtülmüş bir kabuğun üstüne ikinci bir örtü
 * koymak demekti; sayfada da okunan listeyi kapatmadan seçim yapmak daha iyi çalışıyor.
 *
 * SEÇİM ÖNCE ELDEKİ LİSTEYE BAKIYOR. §14b `POST /chat/conversations` birebir konuşmada var olanı
 * döndürür diyor; ölçülen davranış bu değildi — bir kişi seçmek İKİNCİ bir satır üretti ve aynı
 * kişi listede iki kez göründü. Cevap elimizdeyken yazma isteği göndermenin faydası yok.
 *
 * KONUŞMA YOKSA TASLAK AÇILIYOR, konuşma değil (denetim Sohbet 18). Kişiye basmak eskiden
 * `POST /chat/conversations` gönderiyordu: hiçbir şey yazmadan vazgeçen kişi karşı tarafın
 * listesine boş bir konuşma bırakıyordu. Şimdi çağıran bir taslak açıyor (`onStartDraft`) ve
 * konuşma ilk mesajla oluşuyor. Grup kipi değişmedi: grubu kurmak bilinçli, ayrı bir karar.
 *
 * HİÇBİR SATIRDA HOVER YOK: seçim odakla, basmayla ve seçili işaretiyle belli oluyor — bu yüzey
 * tablette de kullanılıyor ve orada fare yok.
 */
export function NewConversationPane({
  onCancel,
  onCreated,
  existingWith,
  onStartDraft,
}: {
  onCancel: () => void;
  /** Var olan bir birebir konuşma seçildi ya da grup oluşturuldu. */
  onCreated: (conversationId: string) => void;
  /** O kişiyle zaten bir birebir konuşma varsa kimliği; yeni açmak yerine ona gidiliyor. */
  existingWith: (userId: string) => string | null;
  /** Konuşması olmayan bir kişi seçildi: taslak aç, sunucuya henüz bir şey yazma. */
  onStartDraft: (person: DirectoryPersonRow) => void;
}) {
  const queryClient = useQueryClient();
  const { permissions, session } = useAuth();
  const canManage = permissions.can(CHAT_PERMISSIONS.manage);
  /**
   * KİŞİNİN KENDİSİ (8 Ekim 2026, kullanılabilirlik turu). Rehber araması kişiyi kendisini de
   * döndürüyor ve listede işaretsiz duruyordu: kendi adını başkasınınki sanıp seçen kişi kendine
   * yazıyordu. Birebirde kendine yazmak bilinçli bir olanak (not defteri gibi; sunucu izin veriyor),
   * o yüzden satır KALIYOR ama «(siz)» diye işaretli. Grupta HİÇ yok: grubu kuran zaten üyesi,
   * kendini üye olarak seçmek bir şey değiştirmiyor ama değiştiriyormuş gibi görünüyordu.
   */
  const meId = session?.user.id ?? null;

  const [mode, setMode] = useState<"direct" | "group">("direct");
  const [term, setTerm] = useState("");

  /**
   * BÖLME AÇILINCA ODAK KİŞİ ARAMASINDA (9 Ekim 2026, 3. tur). Bölme hep bir basışla açılıyor
   * («Yeni sohbet»); odak basılan düğmede kalıyordu ve klavyeyle gelen kişi «Kişi ara»ya
   * konuşma listesinin içinden yedi Tab'la varıyordu. Dokunmatik ekranda DEĞİL: orada odak
   * klavyeyi açar ve seçilecek kişi listesinin yarısını örter — dokunan kişi listeden seçiyor.
   */
  const searchHost = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const coarse =
      typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)")?.matches === true;
    if (coarse) return;
    searchHost.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
  }, []);
  const [title, setTitle] = useState("");
  const [picked, setPicked] = useState<DirectoryPersonRow[]>([]);

  /**
   * Boş terimle de çalışıyor: sunucu ilk yirmi kişiyi veriyor. «Aramak için iki harf yazın»
   * diyen bir liste, kimin kayıtlı olduğunu bilmeyen birini hiç başlatamaz.
   */
  const people = useQuery(chatPeopleQuery(term));

  const create = useMutation({
    mutationFn: () =>
      chatApi.createGroup({
        title: title.trim(),
        memberUserIds: picked.map((person) => person.userId),
      }),
    onSuccess: async (conversation) => {
      // Önce ekran, sonra veri: seçilen konuşma açılıyor, listeler arkadan yetişiyor.
      onCreated(conversation.id);
      await invalidateChat(queryClient);
    },
    onError: (error) => toast.errorMessage("Grup açılamadı", chatErrorMessage(error)),
  });

  /**
   * Arama sonucu ARTI seçilmiş olup sonuçta görünmeyenler. Grup kipinde seçtiğin kişi, terimi
   * değiştirince listeden düşerdi ve seçili olduğunu gösteren hiçbir şey kalmazdı.
   */
  const rows = (() => {
    const found = people.data?.rows ?? [];
    if (mode === "direct") return found;
    const others = found.filter((person) => person.userId !== meId);
    const seen = new Set(others.map((person) => person.userId));
    return [...others, ...picked.filter((person) => !seen.has(person.userId))];
  })();

  const isPicked = (userId: string) => picked.some((person) => person.userId === userId);

  /** Cevap henüz yok ve gösterecek satır da yok: iskelet. Önceki aramanın satırları duruyorsa değil. */
  const peopleLoading = people.error === null && rows.length === 0 && people.isFetching;

  function choose(person: DirectoryPersonRow): void {
    if (mode === "group") {
      setPicked((current) =>
        isPicked(person.userId)
          ? current.filter((candidate) => candidate.userId !== person.userId)
          : [...current, person],
      );
      return;
    }
    const existing = existingWith(person.userId);
    if (existing !== null) {
      onCreated(existing);
      return;
    }
    onStartDraft(person);
  }

  const canCreateGroup = title.trim().length > 0 && picked.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-start gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold text-fg">Yeni sohbet</div>
          <p className="truncate text-xs text-fg-muted">
            {mode === "direct"
              ? "Yazmak istediğiniz kişiyi seçin."
              : "Gruba bir ad verin ve üyeleri seçin."}
          </p>
        </div>
        <Button variant="quiet" size="sm" onClick={onCancel} disabled={create.isPending}>
          Vazgeç
        </Button>
      </header>

      <div className="flex shrink-0 flex-col gap-2 px-3 py-2">
        {canManage ? (
          <SegmentedControl
            ariaLabel="Sohbet türü"
            size="sm"
            value={mode}
            onChange={(next: string) => {
              setMode(next === "group" ? "group" : "direct");
              setPicked([]);
            }}
            options={[
              { value: "direct", label: "Kişi" },
              { value: "group", label: "Grup" },
            ]}
          />
        ) : null}

        {mode === "group" ? (
          <TextField
            label="Grup adı"
            required
            value={title}
            // Sunucunun sınırı (`chat.schema.ts` › `zTitle`, 160): 120 olunca 121–160 arası bir ad
            // ekranda yazılamıyor, başka bir istemciden gelen ad ise kabul ediliyordu (9 Ekim 2026).
            maxLength={160}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Örn. Sevkiyat koordinasyonu"
          />
        ) : null}

        <div ref={searchHost}>
          {/*
            YER TUTUCU ARANAN ŞEYİ SÖYLÜYOR (9 Ekim 2026, 5. tur — liste taraması L1). «Ad ya da unvan»
            yazıyordu; arama rehberin ucuna gidiyor ve o uç ad, kullanıcı adı ve (görünürse) e-postada
            arıyor — unvanda değil (`directory.repository.ts › searchHits`). Unvanla arayan kişi
            «Kişi bulunamadı» görüp aradığı kişinin kayıtlı olmadığını sanıyordu. Rehberin kendi
            arama kutusuyla aynı cümle.
          */}
          <SearchInput
            value={term}
            onChange={setTerm}
            label="Kişi ara"
            placeholder={PEOPLE_SEARCH_PLACEHOLDER}
          />
        </div>
      </div>

      {people.error ? (
        <div className="px-3 pb-3">
          <Banner
            tone="danger"
            title="Kişiler yüklenemedi"
            action={
              <Button size="sm" variant="secondary" onClick={() => void people.refetch()}>
                Yeniden dene
              </Button>
            }
          >
            {chatErrorMessage(people.error)}
          </Banner>
        </div>
      ) : null}

      {/*
        YÜKLENİYOR, BOŞ DEĞİL. Kişiler gelene kadar bu alan bomboştu. `SkeletonFrame` iskeleti
        200 ms bekletiyor (hızlı cevap gri yanıp sönmesin); o pencerede de «kimse yok» DENMİYOR —
        cevabı gelmemiş bir liste hakkında söylenecek bir şey yok.
      */}
      <SkeletonFrame
        isLoading={peopleLoading}
        skeleton={<PeopleSkeleton />}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {peopleLoading ? null : rows.length === 0 && !people.error ? (
          <EmptyState
            variant={term ? "no-results" : "no-data"}
            size="sm"
            title={term ? "Kişi bulunamadı" : "Gösterilecek kişi yok"}
            description={
              term
                ? "Başka bir ad, kullanıcı adı ya da e-posta deneyin."
                : "Rehberde sizinle sohbet edebilecek kimse görünmüyor."
            }
          />
        ) : (
          <ul className="flex flex-col" aria-label="Kişiler">
            {rows.map((person) => (
              <li key={person.userId}>
                {/*
                  Kitin `Button`'u değil: bu bir satır, bir kontrol değil — iki satırlık içerik
                  taşıyor ve `Button` tek satırlık bir etiket için yazılmış. Odak halkasını taban
                  katman çiziyor, yani `:focus-visible` burada da geçerli.
                */}
                <button
                  type="button"
                  disabled={create.isPending}
                  aria-pressed={mode === "group" ? isPicked(person.userId) : undefined}
                  onClick={() => choose(person)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-start active:bg-surface-2 disabled:opacity-60"
                >
                  <Avatar
                    name={person.displayName}
                    size="sm"
                    {...(() => {
                      const src = personPhotoUrl(person.userId, person.hasPhoto, person.photoFileId);
                      return src ? { src } : {};
                    })()}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-fg">
                      {person.displayName}
                      {person.userId === meId ? (
                        <span className="font-normal text-fg-muted"> (siz)</span>
                      ) : null}
                    </span>
                    {personSubtitle(person) ? (
                      <span className="block truncate text-xs text-fg-muted">
                        {personSubtitle(person)}
                      </span>
                    ) : null}
                  </span>
                  {mode === "group" && isPicked(person.userId) ? (
                    <Check size={16} strokeWidth={2} className="shrink-0 text-brand" aria-hidden />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </SkeletonFrame>

      {/*
        Grup kipinin kendi alt şeridi: birebirde seçim ANINDA konuşmayı açıyor, grupta ise
        seçim birikiyor ve bitiş ayrı bir karar. Şerit yalnız o kipte var, yoksa birebir akışta
        basılacak ikinci bir düğme icat etmiş olurduk.
      */}
      {mode === "group" ? (
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border px-3 py-2">
          <span className="text-xs text-fg-muted">
            {picked.length > 0 ? `${String(picked.length)} kişi seçildi` : "Üye seçilmedi"}
          </span>
          <Button
            size="sm"
            loading={create.isPending}
            disabled={!canCreateGroup}
            {...(canCreateGroup ? {} : { disabledReason: "Grup adı yazın ve en az bir üye seçin" })}
            onClick={() => create.mutate()}
          >
            Grubu oluştur
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Kişi satırının iskeleti: avatar + ad + ikinci satır, gerçek satırla aynı yükseklikte. */
function PeopleSkeleton() {
  return (
    <ul aria-hidden="true" className="flex flex-col">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <li key={index} className="flex items-center gap-3 px-3 py-2">
          <SkCircle size={32} />
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            <SkLine w={index % 2 === 0 ? "60%" : "45%"} />
            <SkLine w={index % 2 === 0 ? "35%" : "50%"} />
          </span>
        </li>
      ))}
    </ul>
  );
}
